import { Theme, useMediaQuery } from '@mui/material';
import { type Table } from '@tanstack/react-table';
import { Virtualizer } from '@tanstack/react-virtual';
import React, { useEffect, useMemo, useRef } from 'react';

import { CardConfig } from '@work4all/models/lib/table-schema/card-config';

import { reactRefSetter } from '@work4all/utils';

import {
  LEFT_PINNED_COLUMN_SHADOW_PROPERTY,
  LEFT_PINNED_COLUMN_SHADOW_VALUE,
  RIGHT_PINNED_COLUMN_SHADOW_PROPERTY,
  RIGHT_PINNED_COLUMN_SHADOW_VALUE,
} from './components/get-common-pinning-styles';
import {
  RowVirtualizerV8,
  RowVirtualizerV8Props,
} from './components/loader/RowVirtualizerV8';
import { Table as TableComponent } from './components/table/Table';
import { TableBody } from './components/table-body/TableBody';
import { TableFooterContext } from './components/table-footer/TableFooterContext';
import { TableFooterV8 } from './components/table-footer/TableFooterV8';
import { TableHeaderV8 } from './components/table-header/TableHeaderV8';
import { useSelectionRowClickV8 } from './hooks/use-selection-row-click-v8';
import { ICssClasses } from './types';

export interface VirtualizedTableV8Props
  extends Pick<
    RowVirtualizerV8Props,
    | 'bottomPadding'
    | 'fetchMore'
    | 'onRowClick'
    | 'onRowDoubleClick'
    | 'onRowContextMenu'
    | 'allowAutoHeight'
  > {
  cardsView?: boolean;
  cardConfig?: CardConfig | null;
  table: Table<any>;
  classes?: ICssClasses;
  rowHeightRem?: RowVirtualizerV8Props['rowHeightRem'];
  noHeaderSeperator?: boolean;
  className?: string;
  displayFooter?: boolean;
  pending?: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
  /**
   * Render custom content when table has 0 rows and `pending` prop is false.
   * This content will replace normal table body.
   */
  noRowsRenderer?: () => React.ReactNode;
  hideHeaderTooltip?: boolean;
  footerCellProps?: Record<string, number>;
}

export function VirtualizedTableV8(props: VirtualizedTableV8Props) {
  const {
    table,
    className,
    cardsView = false,
    cardConfig = null,
    displayFooter = false,
    pending,
    noRowsRenderer,
    hideHeaderTooltip,
    scrollRef,
    noHeaderSeperator,
    classes,
    rowHeightRem,
    bottomPadding,
    fetchMore,
    onRowClick,
    onRowDoubleClick,
    onRowContextMenu,
    footerCellProps,
    allowAutoHeight,
  } = props;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);

  const handleRowClick = useSelectionRowClickV8({ tableBodyRef, table });

  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'));

  // Add box shadow to pinned columns when scrolling
  const handleTableBodyScroll = (element: HTMLElement): void => {
    const { scrollLeft, scrollWidth, clientWidth } = element;

    if (scrollLeft > 0) {
      element.style.setProperty(
        LEFT_PINNED_COLUMN_SHADOW_PROPERTY,
        LEFT_PINNED_COLUMN_SHADOW_VALUE
      );
    } else {
      element.style.removeProperty(LEFT_PINNED_COLUMN_SHADOW_PROPERTY);
    }

    if (clientWidth + scrollLeft < scrollWidth) {
      element.style.setProperty(
        RIGHT_PINNED_COLUMN_SHADOW_PROPERTY,
        RIGHT_PINNED_COLUMN_SHADOW_VALUE
      );
    } else {
      element.style.removeProperty(RIGHT_PINNED_COLUMN_SHADOW_PROPERTY);
    }
  };

  useEffect(() => {
    if (!tableContainerRef.current) return;
    handleTableBodyScroll(tableContainerRef.current);
  });

  const mergedRef = useMemo(
    () => reactRefSetter(tableContainerRef, scrollRef),
    [tableContainerRef, scrollRef]
  );

  const hasNoRows =
    pending === false && table.getRowCount() === 0 && !!noRowsRenderer;

  const virtualizerRef =
    useRef<Virtualizer<HTMLDivElement, HTMLDivElement>>(null);

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.code === 'ArrowUp' || e.code === 'ArrowDown') {
      e.preventDefault();
      const rowVirtualizer = virtualizerRef.current;
      if (!rowVirtualizer) return;
      const rows = table.getRowModel().rows;
      const selectedRowModel = table.getSelectedRowModel();
      const selectedRow = selectedRowModel.rows[0];
      if (selectedRowModel.rows.length > 1) return;
      let rowToActivateIdx = 0;
      if (selectedRow) {
        const activeRowIdx = selectedRow.index;
        if (activeRowIdx !== 0 || activeRowIdx < rows.length) {
          rowToActivateIdx = Math.min(
            rows.length - 1,
            Math.max(
              0,
              e.code === 'ArrowUp' ? activeRowIdx - 1 : activeRowIdx + 1
            )
          );
        }
      }
      rowVirtualizer.scrollToIndex(rowToActivateIdx);
      table.setRowSelection({ [rows[rowToActivateIdx].id]: true });
    }
  }

  return (
    <TableComponent
      ref={mergedRef}
      className={className}
      style={{
        overflow: 'auto',
        position: 'relative',
        height: '100%',
      }}
      onScroll={(event) => handleTableBodyScroll(event.currentTarget)}
      onKeyDown={handleKeyDown}
    >
      {!cardsView && (
        <TableHeaderV8
          flatColumns={table.getVisibleFlatColumns()}
          headerGroups={table.getHeaderGroups()}
          setColumnOrder={table.setColumnOrder}
          noSeperator={noHeaderSeperator}
          classes={classes}
          width={table.getTotalSize()}
          groupBy={table.getState().grouping}
          hideHeaderTooltip={hideHeaderTooltip}
        />
      )}

      <TableBody
        ref={tableBodyRef}
        className={classes?.tableBody}
        role="rowgroup"
      >
        {hasNoRows ? (
          noRowsRenderer()
        ) : (
          <RowVirtualizerV8
            virtualizerRef={virtualizerRef}
            table={table}
            cardsView={cardsView}
            tableContainerRef={tableContainerRef}
            cardConfig={cardConfig}
            fetchMore={fetchMore}
            onRowDoubleClick={onRowDoubleClick}
            onRowClick={onRowClick ?? handleRowClick}
            onRowContextMenu={onRowContextMenu}
            classes={classes}
            rowHeightRem={rowHeightRem}
            bottomPadding={bottomPadding}
            ignoreLongPress={isDesktop}
            allowAutoHeight={allowAutoHeight}
          />
        )}
      </TableBody>

      {displayFooter && !cardsView && (
        <TableFooterContext data={table.options.footerData}>
          <TableFooterV8
            headerGroups={table.getHeaderGroups()}
            classes={classes}
            grouping={table.getState().grouping}
            cellProps={footerCellProps}
          />
        </TableFooterContext>
      )}
    </TableComponent>
  );
}
